import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight, ChevronLeft, Check, Wrench, Globe,
  Share2, Building2, Sparkles, Plus, X, Loader2, GalleryHorizontal, LogOut
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import ImageUpload from '@/components/ImageUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const ONBOARDING_KEY = 'contractor_onboarding_done';

const STEPS = [
  { id: 'welcome',   label: 'Welcome',    icon: Sparkles },
  { id: 'company',   label: 'Company',    icon: Building2 },
  { id: 'services',  label: 'Services',   icon: Wrench },
  { id: 'coverage',  label: 'Coverage',   icon: Globe },
  { id: 'finish',    label: 'Finish',     icon: GalleryHorizontal },
];

const SERVICE_OPTIONS = [
  'Design', 'Build', 'Installation', 'Dismantling', 'Rental', 'Logistics',
  'AV & Technology', 'Electrical', 'Rigging', 'Graphics & Print',
  'Furniture', 'Flooring', 'Lighting', 'Project Management',
];

const COUNTRY_PRESETS = [
  { name: 'Germany',        flag: '🇩🇪', cities: ['Frankfurt', 'Munich', 'Hannover', 'Düsseldorf'] },
  { name: 'Turkey',         flag: '🇹🇷', cities: ['Istanbul', 'Ankara', 'Izmir'] },
  { name: 'UAE',            flag: '🇦🇪', cities: ['Dubai', 'Abu Dhabi'] },
  { name: 'Italy',          flag: '🇮🇹', cities: ['Milan', 'Bologna', 'Rome'] },
  { name: 'France',         flag: '🇫🇷', cities: ['Paris', 'Lyon', 'Cannes'] },
  { name: 'United Kingdom', flag: '🇬🇧', cities: ['London', 'Birmingham', 'Manchester'] },
  { name: 'Netherlands',    flag: '🇳🇱', cities: ['Amsterdam', 'Utrecht'] },
  { name: 'Spain',          flag: '🇪🇸', cities: ['Barcelona', 'Madrid'] },
  { name: 'Saudi Arabia',   flag: '🇸🇦', cities: ['Riyadh', 'Jeddah'] },
  { name: 'Switzerland',    flag: '🇨🇭', cities: ['Basel', 'Geneva', 'Zurich'] },
];

const inputCls = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white transition-shadow focus:shadow-md';
const labelCls = 'block text-sm font-semibold text-gray-700 mb-1.5';

export default function ContractorOnboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const contractorMembership = user?.org_memberships?.find(m => m.role?.startsWith('CONTRACTOR'));
  const companyName = contractorMembership?.org_name || '';
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [autoSaved, setAutoSaved] = useState(false);

  const autoSave = async (currentStep) => {
    const token = getToken();
    if (!token) return;
    try {
      let payload = {};
      if (currentStep === 1) {
        payload = { tagline, about, logo_url: logoUrl, social: { website, linkedin, instagram: '', twitter: '', youtube: '' } };
      } else if (currentStep === 2) {
        payload = { services };
      } else if (currentStep === 3) {
        payload = {
          coverage: { countries, active_shows: [] },
          capabilities: { max_booth_sqm: parseInt(maxSqm) || 100, avg_budget_min: 5000, avg_budget_max: 50000, currency: 'EUR', lead_time_weeks: 4 },
        };
      }
      if (Object.keys(payload).length === 0) return;
      await axios.patch(`${API}/api/contractor/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setAutoSaved(true);
      setTimeout(() => setAutoSaved(false), 2000);
    } catch {}
  };

  const [tagline, setTagline] = useState('');
  const [about, setAbout] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [services, setServices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [website, setWebsite] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [maxSqm, setMaxSqm] = useState('');

  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  const fillTestData = () => {
    setTagline('Award-winning stand builders with 15+ years of experience across Europe and Middle East');
    setAbout('Prostand Display Solutions has been delivering exceptional exhibition stands for clients across 30+ countries. From conceptual design to complete build-out, we manage every detail. Our portfolio includes projects for Fortune 500 companies, government pavilions, and boutique luxury brands at events including Hannover Messe, GITEX, and Automechanika.');
    setWebsite('prostand-display.de');
    setLinkedin('linkedin.com/company/prostand-display');
    setCountries(['Germany', 'Netherlands', 'UAE']);
    setServices(['Stand Design', 'Custom Build', 'Modular Stands', 'Branding & Graphics']);
    setMaxSqm('500');
  };

  const skipOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    localStorage.removeItem(`${ONBOARDING_KEY}_banner_dismissed`);
    navigate('/app/contractor/dashboard');
  };

  const handleNext = async () => {
    // Step 1 (Company): tagline required
    if (step === 1 && !tagline.trim()) {
      setError(t('validation.required') + ' — Tagline alanı zorunlu');
      return;
    }
    setError('');
    if (step < STEPS.length - 1) {
      await autoSave(step);
      setStep(s => s + 1);
    } else {
      await handleFinish();
    }
  };

  const handleFinish = async () => {
    setSaving(true);
    setError('');
    try {
      const token = getToken();
      const payload = {
        tagline,
        about,
        logo_url: logoUrl,
        services,
        coverage: { countries, active_shows: [] },
        social: { website, linkedin, instagram: '', twitter: '', youtube: '' },
        capabilities: { max_booth_sqm: parseInt(maxSqm) || 100, avg_budget_min: 5000, avg_budget_max: 50000, currency: 'EUR', lead_time_weeks: 4 },
      };
      await axios.patch(`${API}/api/contractor/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.setItem(ONBOARDING_KEY, '1');
      localStorage.removeItem(`${ONBOARDING_KEY}_banner_dismissed`);
      navigate('/app/contractor/dashboard');
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleCountry = (preset) => {
    const exists = countries.find(c => c.name === preset.name);
    if (exists) {
      setCountries(prev => prev.filter(c => c.name !== preset.name));
    } else {
      setCountries(prev => [...prev, { flag: preset.flag, name: preset.name, cities: preset.cities, primary: false }]);
    }
  };

  const toggleService = (svc) => {
    if (services.includes(svc)) {
      setServices(prev => prev.filter(s => s !== svc));
    } else {
      setServices(prev => [...prev, svc]);
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;
  const isLastStep = step === STEPS.length - 1;
  const isFirstStep = step === 0;

  // Load existing profile data on mount
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    axios.get(`${API}/api/contractor/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const p = r.data?.profile || {};
        if (p.tagline) setTagline(p.tagline);
        if (p.about) setAbout(p.about);
        if (p.logo_url) setLogoUrl(p.logo_url);
        if (p.social?.website) setWebsite(p.social.website);
        if (p.social?.linkedin) setLinkedin(p.social.linkedin);
        if (Array.isArray(p.services) && p.services.length > 0) setServices(p.services);
        if (p.capabilities?.max_booth_sqm) setMaxSqm(String(p.capabilities.max_booth_sqm));
        // Restore countries from territories
        const terr = p.territories || p.coverage?.countries || [];
        if (terr.length > 0) setCountries(terr);
      }).catch(() => {});
  // eslint-disable-next-line
  }, []);

  // ── Step Renders ──────────────────────────────────────────────────────────
  const stepWelcome = (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto shadow-xl">
        <Sparkles className="h-10 w-10 text-white" />
      </div>
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Welcome to ExpoStand!</h2>
        <p className="text-gray-500 text-base leading-relaxed max-w-md mx-auto">
          Set up your contractor profile in just a few steps. A complete profile helps you win more briefs and stand out from the competition.
        </p>
      </div>

      {/* Social Proof */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-100 max-w-lg mx-auto">
        <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-4">Why complete your profile?</p>
        <div className="grid grid-cols-3 gap-4">
          {[
            { stat: '4×', label: 'more brief matches with complete coverage data' },
            { stat: '68%', label: 'of exhibitors choose contractors based on portfolio' },
            { stat: '2days', label: 'avg. time to first proposal for verified profiles' },
          ].map(({ stat, label }) => (
            <div key={stat} className="text-center">
              <p className="text-2xl font-black text-green-700">{stat}</p>
              <p className="text-xs text-gray-500 mt-1 leading-snug">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm max-w-lg mx-auto">
        {[
          { icon: Wrench, title: 'Showcase Services', desc: 'What you build & deliver' },
          { icon: Globe, title: 'Set Coverage', desc: 'Trade shows & countries' },
          { icon: Share2, title: 'Win More Briefs', desc: 'Match with exhibitors' },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} className="bg-green-50 rounded-xl p-4 border border-green-100">
            <Icon className="h-5 w-5 text-green-600 mx-auto mb-2" />
            <p className="font-semibold text-gray-800 text-xs">{title}</p>
            <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">Takes about 3 minutes · Skip anytime</p>
        <button type="button" onClick={fillTestData}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-green-400/50 text-green-600 text-xs font-semibold hover:bg-green-50 transition-colors">
          ⚡ Fill Test Data
        </button>
      </div>
    </div>
  );

  const stepCompany = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">About Your Company</h2>
        <p className="text-gray-500 text-sm">Tell exhibitors who you are and what makes you great.</p>
      </div>
      <div>
        <label className={labelCls}>Tagline <span className="text-gray-400 font-normal">(1-line pitch)</span></label>
        <input className={inputCls} value={tagline} onChange={e => setTagline(e.target.value)}
          placeholder="e.g. Award-winning exhibition stand builders across Europe & Middle East" />
      </div>
      <div>
        <label className={labelCls}>About <span className="text-gray-400 font-normal">(optional)</span></label>
        <textarea className={`${inputCls} resize-none`} rows={7} value={about} onChange={e => setAbout(e.target.value)}
          placeholder="Describe your company, expertise, and key competitive advantages..." />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <ImageUpload
            label="Company Logo"
            hint="PNG, JPG, WEBP — Max 5MB"
            aspectRatio="logo"
            value={logoUrl}
            onChange={setLogoUrl}
            token={getToken()}
            data-testid="contractor-onboarding-logo-upload"
          />
        </div>
        <div>
          <label className={labelCls}>Max Booth Size (m²)</label>
          <input type="number" className={inputCls} value={maxSqm} onChange={e => setMaxSqm(e.target.value)}
            placeholder="e.g. 500" />
        </div>
      </div>
    </div>
  );

  const stepServices = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">What Do You Offer?</h2>
        <p className="text-gray-500 text-sm">Select the services you provide. This affects your match score on open briefs.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {SERVICE_OPTIONS.map(svc => {
          const selected = services.includes(svc);
          return (
            <button key={svc} onClick={() => toggleService(svc)}
              className={`text-xs px-3 py-2 rounded-full border font-medium transition-all ${selected
                ? 'bg-green-600 text-white border-green-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:border-green-400 hover:text-green-700'
              }`}>
              {selected && <Check className="h-3 w-3 inline mr-1" />}{svc}
            </button>
          );
        })}
      </div>
      {services.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2">Selected ({services.length})</p>
          <div className="flex flex-wrap gap-2">
            {services.map(svc => (
              <span key={svc} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-sm text-green-700 font-medium">
                {svc}
                <button onClick={() => setServices(p => p.filter(s => s !== svc))} className="text-green-400 hover:text-green-700">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const stepCoverage = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Where Do You Operate?</h2>
        <p className="text-gray-500 text-sm">Select countries with major trade shows you work in. This powers your Match Score.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {COUNTRY_PRESETS.map(preset => {
          const selected = countries.find(c => c.name === preset.name);
          return (
            <button key={preset.name} onClick={() => toggleCountry(preset)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${selected
                ? 'bg-green-600 text-white border-green-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-200 hover:border-green-400 hover:text-green-700'
              }`}>
              <span className="text-base">{preset.flag}</span>
              <span className="truncate">{preset.name}</span>
              {selected && <Check className="h-3.5 w-3.5 ml-auto flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      {countries.length > 0 && (
        <p className="text-xs text-green-600 font-medium">{countries.length} {countries.length === 1 ? 'country' : 'countries'} selected</p>
      )}
    </div>
  );

  const stepFinish = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Almost Done!</h2>
        <p className="text-gray-500 text-sm">Add your web presence so exhibitors can learn more about you.</p>
      </div>
      <div>
        <label className={labelCls}>Website</label>
        <input className={inputCls} value={website} onChange={e => setWebsite(e.target.value)}
          placeholder="https://yourcompany.com" />
      </div>
      <div>
        <label className={labelCls}>LinkedIn Company Page <span className="text-gray-400 font-normal">(optional)</span></label>
        <input className={inputCls} value={linkedin} onChange={e => setLinkedin(e.target.value)}
          placeholder="https://linkedin.com/company/..." />
      </div>
      {/* Summary */}
      <div className="bg-green-50 rounded-xl p-4 border border-green-100 space-y-2">
        <p className="text-sm font-bold text-green-800 mb-2">Your Profile Summary</p>
        {[
          { label: 'Tagline',   value: tagline || '—',   ok: Boolean(tagline) },
          { label: 'About',     value: about ? 'Added' : '—', ok: Boolean(about) },
          { label: 'Services',  value: services.length > 0 ? `${services.length} services` : '—', ok: services.length > 0 },
          { label: 'Coverage',  value: countries.length > 0 ? `${countries.length} countries` : '—', ok: countries.length > 0 },
          { label: 'Website',   value: website || '—',   ok: Boolean(website) },
        ].map(({ label, value, ok }) => (
          <div key={label} className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{label}</span>
            <span className={`font-medium ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
              {ok && <Check className="h-3.5 w-3.5 inline mr-1" />}{value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 0: return stepWelcome;
      case 1: return stepCompany;
      case 2: return stepServices;
      case 3: return stepCoverage;
      case 4: return stepFinish;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex flex-col w-80 xl:w-96 bg-gradient-to-b from-green-800 to-green-600 p-8 text-white flex-shrink-0">
        <div className="flex items-center gap-3 mb-12">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <Wrench className="h-4 w-4" />
          </div>
          <span className="font-bold text-lg">ExpoStand</span>
          <span className="text-green-300 text-sm ml-1">Contractor</span>
        </div>

        <div className="space-y-3 flex-1">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <div key={s.id} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${active ? 'bg-white/15' : 'opacity-60'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                  done ? 'bg-emerald-400 border-emerald-400' : active ? 'bg-white/20 border-white' : 'border-white/40'
                }`}>
                  {done ? <Check className="h-4 w-4 text-white" /> : <Icon className="h-4 w-4" />}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${active ? 'text-white' : 'text-white/80'}`}>{s.label}</p>
                  {active && <p className="text-xs text-green-300">Current step</p>}
                  {done && <p className="text-xs text-emerald-300">Completed</p>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8">
          <div className="flex justify-between text-xs text-green-300 mb-2">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2">
            <div className="bg-white rounded-full h-2 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* User info */}
        <div className="mt-6 pt-5 border-t border-white/20">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0 text-white font-bold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0">
              <p className="text-white text-xs font-semibold truncate">{companyName || 'Şirket Adı'}</p>
              <p className="text-green-200 text-xs truncate">{user?.name || '—'}</p>
              <p className="text-green-300 text-xs truncate">{user?.email || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col">
        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between px-10 py-3.5 border-b bg-white shadow-sm">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-green-600 flex items-center justify-center">
              <Wrench className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-gray-900 text-sm">ExpoStand</span>
            <span className="text-gray-400 text-sm">·</span>
            <span className="text-green-600 text-sm font-medium">Contractor Onboarding</span>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors"
            data-testid="onboarding-signout-btn"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b bg-white">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-green-600 flex items-center justify-center">
              <Wrench className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-bold text-gray-900">ExpoStand Contractor</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={skipOnboarding} className="text-xs text-gray-400 hover:text-gray-600">Skip</button>
            <button onClick={logout} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600">
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>

        {/* Mobile Progress */}
        <div className="lg:hidden px-4 pt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Step {step + 1} of {STEPS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="bg-green-600 h-1.5 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-lg">
            {renderStep()}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 lg:px-12 border-t bg-white">
          <div className="max-w-lg mx-auto flex items-center justify-end gap-4">
            <div className="flex gap-3">
              {!isFirstStep && (
                <Button variant="outline" onClick={() => setStep(s => s - 1)} className="gap-2" data-testid="onboarding-back-btn">
                  <ChevronLeft className="h-4 w-4" /> Back
                </Button>
              )}
              {isFirstStep && (
                <Button variant="outline" onClick={skipOnboarding} className="text-gray-500" data-testid="onboarding-skip-btn">
                  Skip setup
                </Button>
              )}
              <Button
                onClick={handleNext}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 text-white gap-2 min-w-[120px]"
                data-testid="onboarding-next-btn"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>
                ) : isLastStep ? (
                  <><Check className="h-4 w-4" /> Complete Setup</>
                ) : (
                  <>Continue <ChevronRight className="h-4 w-4" /></>
                )}
              </Button>
              {autoSaved && (
                <span className="text-xs text-green-600 font-medium flex items-center gap-1" data-testid="autosave-indicator">
                  <Check className="h-3 w-3" /> Kaydedildi
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
